import { useId } from 'react';
import { t } from '../../i18n/i18n';
import './LineChartSkeleton.scss';

/**
 * Catmull-Rom spline to cubic Bezier path converter.
 * Produces silky smooth curves through control points without harsh angles.
 */
function getSmoothPath(points) {
  if (points.length < 2) return '';
  let d = `M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(i - 1, 0)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(i + 2, points.length - 1)];

    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;

    d += ` C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(1)} ${cp2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
  }
  return d;
}

const NORM_POINTS_FULL = [
  [0.0, 0.22],
  [0.12, 0.36],
  [0.24, 0.3],
  [0.38, 0.55],
  [0.5, 0.48],
  [0.64, 0.76],
  [0.76, 0.68],
  [0.88, 0.88],
  [1.0, 0.92],
];

const NORM_POINTS_SPARKLINE = [
  [0.0, 0.25],
  [0.16, 0.45],
  [0.32, 0.35],
  [0.48, 0.65],
  [0.64, 0.52],
  [0.82, 0.84],
  [1.0, 0.9],
];

const NORM_POINTS_SECONDARY = [
  [0.0, 0.16],
  [0.14, 0.24],
  [0.28, 0.2],
  [0.42, 0.38],
  [0.56, 0.34],
  [0.7, 0.52],
  [0.84, 0.5],
  [1.0, 0.68],
];

/**
 * LineChartSkeleton — A specialized SVG skeleton placeholder that visually mirrors
 * a live line chart (gridlines, Y/X-axis label placeholders, smooth wavy equity curve,
 * vertical gradient area fill, cursor indicator with pulsing marker dot and ghost
 * tooltip, and an animated shimmer sweep).
 *
 * @param {{
 *   height?: number | string,
 *   width?: number | string,
 *   variant?: 'full' | 'sparkline',
 *   showGridLines?: boolean,
 *   showAxes?: boolean,
 *   showArea?: boolean,
 *   showMarker?: boolean,
 *   showSecondaryLine?: boolean,
 *   className?: string,
 *   style?: React.CSSProperties,
 * }} props
 */
export default function LineChartSkeleton({
  height = 220,
  width = '100%',
  variant = 'full',
  showGridLines = true,
  showAxes = true,
  showArea = true,
  showMarker = true,
  showSecondaryLine = false,
  className = '',
  style = {},
}) {
  const rawId = useId();
  const id = rawId.replace(/[^a-zA-Z0-9_-]/g, '');

  const isSparkline = variant === 'sparkline';
  const numericHeight = typeof height === 'number' ? height : parseInt(height, 10) || (isSparkline ? 40 : 220);
  const viewBoxWidth = isSparkline ? 120 : 600;

  const hasAxes = !isSparkline && showAxes;
  const hasGrid = !isSparkline && showGridLines;
  const hasMarker = !isSparkline && showMarker;

  const padLeft = isSparkline ? 2 : hasAxes ? 44 : 16;
  const padRight = isSparkline ? 2 : 16;
  const padTop = isSparkline ? 4 : 24;
  const padBottom = isSparkline ? 4 : hasAxes ? 28 : 12;

  const plotWidth = viewBoxWidth - padLeft - padRight;
  const plotHeight = Math.max(numericHeight - padTop - padBottom, 20);
  const baselineY = padTop + plotHeight;

  // Compute curve points
  const rawNorm = isSparkline ? NORM_POINTS_SPARKLINE : NORM_POINTS_FULL;
  const points = rawNorm.map(([nx, ny]) => ({
    x: padLeft + nx * plotWidth,
    y: baselineY - ny * plotHeight,
  }));

  const mainPath = getSmoothPath(points);
  const areaPath = `${mainPath} L ${(padLeft + plotWidth).toFixed(1)} ${baselineY.toFixed(1)} L ${padLeft.toFixed(1)} ${baselineY.toFixed(1)} Z`;

  // Secondary curve points (if enabled)
  const secondaryPoints = NORM_POINTS_SECONDARY.map(([nx, ny]) => ({
    x: padLeft + nx * plotWidth,
    y: baselineY - ny * plotHeight,
  }));
  const secondaryPath = getSmoothPath(secondaryPoints);

  // Marker point (placed around 76% across the plot)
  const markerPoint = points[Math.min(6, points.length - 1)];

  // Grid line counts (4 horizontal lines)
  const gridLines = [0, 1, 2, 3].map((i) => padTop + (plotHeight * i) / 3);

  // X-axis tick positions (5 ticks)
  const xTicks = [0, 1, 2, 3, 4].map((i) => padLeft + (plotWidth * i) / 4);

  const inlineStyles = {
    width,
    height: typeof height === 'number' ? `${height}px` : height,
    aspectRatio: `${viewBoxWidth} / ${numericHeight}`,
    ...style,
  };

  const areaGradientId = `skel-area-grad-${id}`;

  return (
    <div
      className={`ac-line-chart-skel ac-line-chart-skel--${variant} ${className}`.trim()}
      style={inlineStyles}
      aria-busy="true"
      aria-label={t('Loading chart')}
      role="img"
    >
      <svg
        className="ac-line-chart-skel__svg"
        viewBox={`0 0 ${viewBoxWidth} ${numericHeight}`}
        preserveAspectRatio="none"
        focusable="false"
      >
        <defs>
          <linearGradient id={areaGradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ffffff" stopOpacity={0.08} />
            <stop offset="75%" stopColor="#ffffff" stopOpacity={0.02} />
            <stop offset="100%" stopColor="#000000" stopOpacity={0} />
          </linearGradient>
        </defs>

        {/* 1. Horizontal Gridlines & Y-Axis Label Placeholders */}
        {hasGrid &&
          gridLines.map((y, idx) => (
            <line
              key={`grid-${idx}`}
              x1={padLeft}
              y1={y}
              x2={padLeft + plotWidth}
              y2={y}
              className="ac-line-chart-skel__grid"
            />
          ))}

        {hasAxes &&
          gridLines.map((y, idx) => (
            <rect
              key={`y-label-${idx}`}
              x={8}
              y={y - 4}
              width={26}
              height={8}
              className="ac-line-chart-skel__pill"
            />
          ))}

        {/* 2. X-Axis Label Placeholders */}
        {hasAxes &&
          xTicks.map((x, idx) => {
            const width = 30;
            const adjustedX = idx === 0 ? x : idx === xTicks.length - 1 ? x - width : x - width / 2;
            return (
              <rect
                key={`x-label-${idx}`}
                x={adjustedX}
                y={baselineY + 10}
                width={width}
                height={7}
                className="ac-line-chart-skel__pill"
              />
            );
          })}

        {/* 3. Area Fill */}
        {showArea && (
          <path d={areaPath} fill={`url(#${areaGradientId})`} className="ac-line-chart-skel__area" />
        )}

        {/* 4. Secondary Line Curve (Dual-line equity/balance mode) */}
        {showSecondaryLine && (
          <path d={secondaryPath} className="ac-line-chart-skel__line--secondary" />
        )}

        {/* 5. Main Wavy Curve */}
        <path d={mainPath} className="ac-line-chart-skel__line" />

        {/* 6. Active Guide, Marker Dot & Ghost Tooltip */}
        {hasMarker && markerPoint && (
          <g className="ac-line-chart-skel__marker-group">
            {/* Dashed vertical hover guide */}
            <line
              x1={markerPoint.x}
              y1={padTop}
              x2={markerPoint.x}
              y2={baselineY}
              className="ac-line-chart-skel__guide"
            />

            {/* Ghost Tooltip Badge */}
            <g transform={`translate(${markerPoint.x - 26}, ${markerPoint.y - 25})`}>
              <rect
                x="0"
                y="0"
                width="52"
                height="18"
                className="ac-line-chart-skel__tooltip-box"
              />
              <rect
                x="10"
                y="6"
                width="32"
                height="5"
                className="ac-line-chart-skel__tooltip-bar"
              />
            </g>

            {/* Pulsing Radar Ring & Center Marker Dot */}
            <circle cx={markerPoint.x} cy={markerPoint.y} r="8" className="ac-line-chart-skel__ring" />
            <circle cx={markerPoint.x} cy={markerPoint.y} r="3.5" className="ac-line-chart-skel__dot" />
          </g>
        )}
      </svg>

      {/* Shimmer sweep overlay across the chart */}
      <div className="ac-line-chart-skel__shimmer" aria-hidden="true" />
    </div>
  );
}
